import {
  ContentTilesProps,
  ContentTilesTileProps,
} from '@noths/polaris-client-ribbons-design-system';
import { getContentstackImageURL } from '@noths/polaris-client-utils';
import dynamic from 'next/dynamic';

import { trackImpression, trackLinkClick } from 'src/tracking/components/contentTiles';
import { ContentBlockRenderer } from '../../../types/Block';
import { ContentstackSection } from '../../../types/Contentstack';
import { Transformer } from '../../../types/Transformer';
import { validateNumberOfTiles } from './validation';

interface ContentstackContentTiles extends ContentstackSection {
  items: {
    title: string;
    url: string;
    image: {
      url: string;
    };
    tile_background_color: {
      custom_background_color: boolean;
      background_color: string;
    };
  }[];
  tracking_title: string;
}
interface ContentTileSerializedTile
  extends Omit<ContentTilesTileProps, 'imageSrcGetter' | 'imageSrc'> {
  imageSrc: string;
}

interface ContentTilesSerializedData extends Omit<ContentTilesProps, 'tiles'> {
  trackingTitle: string;
  trackingPosition?: number;
  tiles: ContentTileSerializedTile[];
}

/* istanbul ignore next */
export const renderer: ContentBlockRenderer<ContentTilesSerializedData, ContentTilesProps> = {
  Component: dynamic<ContentTilesProps>(() =>
    import('src/components/blocks/ContentTilesBlock').then((mod) => mod.ContentTilesBlock),
  ),
  getProps: ({ ...serializedData }) => {
    const { trackingPosition, trackingTitle, tiles } = serializedData;

    const dataForTracking = {
      trackingPosition,
      trackingTitle,
      numberOfTiles: tiles.length,
    };
    const showTracking = tiles.length > 0;

    return {
      ...serializedData,
      tiles: tiles.map(({ imageSrc, ...tile }) => {
        return {
          ...tile,
          // @ts-ignore - Could not resolve this error: "Type error: Binding element 'width' implicitly has an 'any' type."
          imageSrc: ({ width, height, aspectRatio }) =>
            getContentstackImageURL(imageSrc, width, { aspectRatio, height }),
        } as ContentTilesTileProps;
      }),
      onVisible: () => (showTracking ? trackImpression(dataForTracking) : undefined),
      onTileClick: (e, { index }) => {
        const tileDataForTracking = {
          ...dataForTracking,
          tile: tiles[index],
          tileIndex: index,
        };
        return showTracking ? trackLinkClick(e, tileDataForTracking) : undefined;
      },
    };
  },
};

export const transformer: Transformer<
  ContentstackContentTiles,
  ContentTilesSerializedData,
  ContentTilesProps
> = ({ _metadata, items, tracking_title }) => ({
  uid: _metadata.uid,
  renderer,
  serializedData: {
    tiles: items.map(({ title, url, image, tile_background_color }) => ({
      title,
      url,
      imageSrc: image.url,
      ...(tile_background_color.custom_background_color && {
        backgroundColor: tile_background_color.background_color,
      }),
    })),
    trackingTitle: tracking_title,
  },
  errors: validateNumberOfTiles(items.length),
});
